import * as React from 'react';

import BadgeIcon from '@mui/icons-material/Badge';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectProps } from '@mui/material/Select';
import { GridRenderCellParams, GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid';

import { CoursePeriodEnrollment } from 'types/periods';

import AttendanceChip from 'components/AttendanceChip';

// array of attendance options
export const ATTENDANCE_VALUES = ['present', 'absent', 'no_permit', 'none'] as const;

interface AttendanceConfig {
  value: 'present' | 'absent' | 'no_permit' | null;
  label: string;
  icon?: React.ReactElement;
  color?: string;
}

export const ATTENDANCE_CONFIG_MAP: Record<'present' | 'absent' | 'no_permit' | 'null', AttendanceConfig> = {
  present: {
    value: 'present',
    label: 'Present',
    icon: <CheckIcon />,
    color: 'success.main',
  },
  absent: {
    value: 'absent',
    label: 'Absent',
    icon: <CloseIcon />,
    color: 'error.main',
  },
  no_permit: {
    value: 'no_permit',
    label: 'No Permit',
    icon: <BadgeIcon />,
    color: 'warning.main',
  },
  null: {
    value: null,
    label: 'None',
  },
};

function EditAttendance(props: GridRenderEditCellParams<CoursePeriodEnrollment, CoursePeriodEnrollment['attendance']>) {
  const { id, field } = props;
  const apiRef = useGridApiContext();

  // change null to 'null'
  const value = String(props.value);

  const handleChange: SelectProps['onChange'] = async (event) => {
    console.log(`EditAttendance Handle Change (id, value): (${id}, ${event.target.value})`);
    const value = event.target.value;
    apiRef.current.setEditCellValue({
      id,
      field,
      // convert string value back to attendance value
      value: value === 'null' ? null : value,
    });
  };

  return (
    <Select
      value={String(value)}
      variant="standard"
      onChange={handleChange}
      sx={{
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          pl: 1,
        },
      }}
      fullWidth
    >
      {Object.values(ATTENDANCE_CONFIG_MAP).map((config) => {
        const value = String(config.value);
        return (
          <MenuItem key={value} value={value}>
            <ListItemIcon sx={{ minWidth: 36, color: config?.color }}>{config?.icon}</ListItemIcon>
            <ListItemText primary={config?.label ?? 'undefined'} sx={{ overflow: 'hidden', color: config?.color }} />
          </MenuItem>
        );
      })}
    </Select>
  );
}

export function renderAttendance(
  params: GridRenderCellParams<CoursePeriodEnrollment, CoursePeriodEnrollment['attendance']>,
) {
  if (params.value == null) {
    return '';
  }

  return <AttendanceChip value={params.value} />;
}

export function renderEditAttendance(
  params: GridRenderEditCellParams<CoursePeriodEnrollment, CoursePeriodEnrollment['attendance']>,
) {
  return <EditAttendance {...params} />;
}
